/**
 * AmenityCategoryGroup — one edit-mode category block (SCRUM-141 / DS-010-5).
 *
 * A category heading (humanized label + graceful category glyph — a new/unknown
 * BE category renders with the title-case fallback label + `sparkles` fallback
 * icon, never crashes) over a `flex flex-wrap` of per-amenity cells. Each cell
 * is a `SelectableChip`; when that amenity's id is in `selectedIds` an inline
 * `AmenityNoteField` appears beneath it (per-amenity flex column).
 *
 * Purely presentational — draft state (`selectedIds` / `notes`) and the
 * mutation live in `AmenitiesEditor`. Toggling a chip calls `onToggle(id)`;
 * editing a note calls `onNoteChange(id, raw)`.
 */

import * as React from 'react';
import { Icon } from '@sparta/ui-kit';
import type { components } from '@artemis/contracts';
import { humanizeCategory } from '../../api/amenities-mapper.js';
import { resolveCategoryIcon } from '../../api/amenity-icons.js';
import { SelectableChip } from './SelectableChip.js';
import { AmenityNoteField } from './AmenityNoteField.js';

type AmenityCatalogItemDto = components['schemas']['AmenityCatalogItemDto'];

export interface AmenityCategoryGroupProps {
  readonly category: string;
  readonly amenities: readonly AmenityCatalogItemDto[];
  readonly selectedIds: ReadonlySet<string>;
  readonly notes: ReadonlyMap<string, string>;
  readonly onToggle: (amenityId: string) => void;
  readonly onNoteChange: (amenityId: string, note: string) => void;
}

export function AmenityCategoryGroup({
  category,
  amenities,
  selectedIds,
  notes,
  onToggle,
  onNoteChange,
}: AmenityCategoryGroupProps): React.ReactElement {
  return (
    <section className="flex flex-col gap-3">
      <h3 className="m-0 flex items-center gap-2 text-sm font-semibold text-[color:var(--fg-primary)]">
        <span aria-hidden="true" className="inline-flex text-[color:var(--fg-tertiary)]">
          <Icon name={resolveCategoryIcon(category)} size={18} />
        </span>
        {humanizeCategory(category)}
      </h3>
      <div className="flex flex-wrap gap-3">
        {amenities.map((amenity) => {
          const selected = selectedIds.has(amenity.id);
          return (
            <div key={amenity.id} className="flex flex-col gap-1.5">
              <SelectableChip
                label={amenity.label}
                selected={selected}
                onToggle={() => onToggle(amenity.id)}
              />
              {selected ? (
                <AmenityNoteField
                  amenityLabel={amenity.label}
                  value={notes.get(amenity.id) ?? ''}
                  onChange={(next) => onNoteChange(amenity.id, next)}
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}
